import { useCallback, useState } from "react";
import type { TrustpilotPromptMilestone } from "@/shared/constants/trustpilot.const";
import {
  getRemainingReviewPlatforms,
  persistReviewPlatformClick,
  persistTrustpilotPromptCompleted,
  shouldQueueTrustpilotPrompt,
} from "@/shared/utils/trustpilotPrompt.util";
import type { ReviewPlatformSource } from "@shared/trustpilot";

/**
 * One-time Trustpilot rating modal for a given milestone.
 * Call `maybeOpen(userId)` after a success event; dismiss / rate marks the milestone shown
 * in localStorage and persists to the DB.
 */
export function useTrustpilotRatingPrompt(milestone: TrustpilotPromptMilestone) {
  const [opened, setOpened] = useState(false);
  const [activeUserId, setActiveUserId] = useState<string | null>(null);
  const [remainingPlatforms, setRemainingPlatforms] = useState<ReviewPlatformSource[]>(["trustpilot"]);

  const maybeOpen = useCallback(
    (userId: string | null | undefined) => {
      if (!userId || !shouldQueueTrustpilotPrompt(userId, milestone)) return;
      const nextPlatforms = getRemainingReviewPlatforms(userId);
      if (nextPlatforms.length === 0) return;
      setActiveUserId(userId);
      setRemainingPlatforms(nextPlatforms);
      setOpened(true);
    },
    [milestone],
  );

  const close = useCallback(() => {
    void persistTrustpilotPromptCompleted(activeUserId, milestone);
    setOpened(false);
  }, [activeUserId, milestone]);

  const onRate = useCallback(
    (platform: ReviewPlatformSource) => {
      // Clicking a platform should only remove that platform, not dismiss the whole milestone.
      void persistReviewPlatformClick(activeUserId, milestone, platform);
      setRemainingPlatforms((prev) => prev.filter((item) => item !== platform));
      setOpened(false);
    },
    [activeUserId, milestone],
  );

  return { opened, maybeOpen, close, onRate, remainingPlatforms };
}
